import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  CircularProgress,
  Avatar,
  Select,
  FormControl,
  Card,
  CardContent,
  Grid,
  Chip,
} from '@mui/material';
import {
  Edit,
  Delete,
  ContentCopy,
  PictureAsPdf,
  Send,
  CheckCircle,
  Cancel,
  Upload,
  GetApp,
  AccessTime,
} from '@mui/icons-material';
import { quotationAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import useDebounce, { usePersistedState } from '../../utils/useDebounce';
import {
  PageHeader,
  ConfirmDialog,
  BulkActionBar,
  StatusChip,
  tableHeadRowStyles,
  tableBodyRowStyles,
  tableAvatarStyles,
  primaryLinkStyles,
  standardTextStyles,
} from '../../components/common';

const QuotationList = () => {
  const navigate = useNavigate();
  const { canDelete, isAdmin, isManager } = useAuth();
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = usePersistedState('quotation_list_page', 0);
  const [rowsPerPage, setRowsPerPage] = usePersistedState('quotation_list_rpp', 10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = usePersistedState('quotation_list_search', '');
  const debouncedSearch = useDebounce(search, 400);
  const [viewMode, setViewMode] = usePersistedState('quotation_list_view', 'list');
  const [statusFilter, setStatusFilter] = usePersistedState('quotation_list_status', '');
  const [locationFilter, setLocationFilter] = usePersistedState('quotation_list_location', '');
  const [mineFilter, setMineFilter] = usePersistedState('quotation_list_mine', true);  // Default to "My Quotations"
  const [locations, setLocations] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [settingsMenuAnchor, setSettingsMenuAnchor] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pdfDownloadLoading, setPdfDownloadLoading] = useState(false);

  // Hidden file input ref for import
  const fileInputRef = React.useRef(null);

  // Handle Export All - Client-side CSV generation
  const handleExportAll = async () => {
    try {
      setExporting(true);
      
      // Fetch all quotations without pagination for export
      const response = await quotationAPI.getAll({
        limit: 10000,
        location: locationFilter || undefined,
      });
      
      const allQuotations = response.data.quotations;
      
      if (allQuotations.length === 0) {
        toast.warning('No quotations to export');
        return;
      }
      
      // Create CSV content
      const headers = [
        'Quotation Number',
        'Date',
        'Customer Name',
        'Customer Email',
        'Customer City',
        'Status',
        'Subtotal',
        'Tax',
        'Grand Total',
        'Created By',
        'Created At'
      ];
      
      const csvRows = allQuotations.map(q => [
        q.quotationNumber || '',
        q.quotationDate ? new Date(q.quotationDate).toLocaleDateString('en-GB') : '',
        q.customer?.name || q.customerSnapshot?.name || '',
        q.customer?.email || q.customerSnapshot?.email || '',
        q.customer?.city || q.customerSnapshot?.address?.city || '',
        q.status || '',
        q.subtotal || 0,
        q.totalTax || 0,
        q.grandTotal || 0,
        q.salesPerson?.name || 'Admin',
        q.createdAt ? new Date(q.createdAt).toLocaleDateString('en-GB') : ''
      ]);
      
      const csvContent = [
        headers.join(','),
        ...csvRows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      ].join('\n');
      
      // Download CSV file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `quotations_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Successfully exported ${allQuotations.length} quotations`);
    } catch (error) {
      toast.error('Failed to export quotations');
      console.error('Export error:', error);
    } finally {
      setExporting(false);
    }
  };

  // Handle Import - trigger file input
  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  // Parse CSV line handling quoted values
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  // Handle file selection for import
  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset input
    event.target.value = '';

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        toast.error('CSV file is empty or has no data rows');
        return;
      }

      // Parse header
      const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase().replace(/"/g, ''));
      
      // Map column names to expected field names
      const columnMap = {
        'customer name': 'customerName',
        'customername': 'customerName',
        'customer': 'customerName',
        'customer email': 'customerEmail',
        'customeremail': 'customerEmail',
        'email': 'customerEmail',
        'customer city': 'customerCity',
        'customercity': 'customerCity',
        'city': 'customerCity',
        'grand total': 'grandTotal',
        'grandtotal': 'grandTotal',
        'total': 'grandTotal',
        'amount': 'grandTotal',
        'title': 'title',
        'description': 'title',
        'subtotal': 'subtotal',
        'sub total': 'subtotal',
        'tax': 'tax',
        'discount': 'discount',
        'date': 'quotationDate',
        'quotation date': 'quotationDate',
        'valid until': 'validUntil',
        'validity': 'validUntil',
        'currency': 'currency',
      };
      
      // Find the customer name column
      const customerNameIndex = headers.findIndex(h => 
        h === 'customer name' || h === 'customername' || h === 'customer'
      );
      
      if (customerNameIndex === -1) {
        toast.error('Missing required column: Customer Name');
        return;
      }
      
      // Parse data rows
      const records = [];
      for (let i = 1; i < lines.length; i++) {
        const values = parseCSVLine(lines[i]);
        if (values.length === 0 || values.every(v => !v)) continue;
        
        const record = {};
        headers.forEach((header, index) => {
          const fieldName = columnMap[header] || header;
          const value = values[index]?.replace(/"/g, '') || '';
          if (value) {
            record[fieldName] = value;
          }
        });
        
        if (record.customerName) {
          records.push(record);
        }
      }
      
      if (records.length === 0) {
        toast.error('No valid records found in CSV');
        return;
      }
      
      // Show importing toast
      toast.info(`Importing ${records.length} records...`);
      
      // Call backend API
      const response = await quotationAPI.import(records);
      
      // Show result
      if (response.data.results?.success > 0) {
        toast.success(`Successfully imported ${response.data.results.success} quotation(s)`);
      }
      
      if (response.data.results?.failed > 0) {
        toast.warning(`${response.data.results.failed} record(s) failed to import`);
        console.log('Failed imports:', response.data.results.errors);
      }
      
      // Refresh the list
      fetchQuotations();
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.message || 'Failed to import quotations');
    }
  };

  // Fetch available locations on mount
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await quotationAPI.getLocations();
        setLocations(response.data.locations || []);
      } catch (error) {
        console.error('Error fetching locations:', error);
      }
    };
    fetchLocations();
  }, []);

  // Skip the initial mount so that restoring `page` from sessionStorage
  // is not immediately clobbered by this reset-to-zero effect.
  const isFirstSearchChange = useRef(true);
  useEffect(() => {
    if (isFirstSearchChange.current) {
      isFirstSearchChange.current = false;
      return;
    }
    setPage(0);
  }, [debouncedSearch]);

  useEffect(() => {
    fetchQuotations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearch, statusFilter, locationFilter, mineFilter]);

  const fetchQuotations = async () => {
    try {
      setLoading(true);
      const response = await quotationAPI.getAll({
        page: page + 1,
        limit: rowsPerPage,
        search: debouncedSearch,
        status: statusFilter || undefined,
        location: locationFilter || undefined,
        mine: mineFilter ? true : undefined,
      });
      setQuotations(response.data.quotations);
      setTotal(response.data.pagination.totalItems);
    } catch (error) {
      toast.error('Failed to load quotations');
      console.error('Error fetching quotations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event, quotation) => {
    setAnchorEl(event.currentTarget);
    setSelectedQuotation(quotation);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedQuotation(null);
  };

  const handleDelete = async () => {
    if (!selectedQuotation) return;
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedQuotation) return;
    try {
      setDeleting(true);
      await quotationAPI.delete(selectedQuotation._id);
      toast.success('Quotation deleted successfully');
      fetchQuotations();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete quotation');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      handleMenuClose();
    }
  };

  const handleBulkDelete = async () => {
    setBulkDeleteDialogOpen(true);
  };

  const confirmBulkDelete = async () => {
    try {
      setDeleting(true);
      await quotationAPI.bulkDelete(selectedRows);
      toast.success(`${selectedRows.length} quotation(s) deleted successfully`);
      setSelectedRows([]);
      fetchQuotations();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete quotations');
    } finally {
      setDeleting(false);
      setBulkDeleteDialogOpen(false);
    }
  };

  const handleDuplicate = async () => {
    if (!selectedQuotation) return;
    
    try {
      const response = await quotationAPI.duplicate(selectedQuotation._id);
      toast.success('Quotation duplicated successfully');
      navigate(`/quotations/${response.data.quotation._id}`);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to duplicate quotation');
    }
    handleMenuClose();
  };

  const handleDownloadPDF = async () => {
    if (!selectedQuotation) return;
    setPdfDownloadLoading(true);
    try {
      const response = await quotationAPI.downloadPDF(selectedQuotation._id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `quotation-${selectedQuotation.quotationNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setPdfDownloadLoading(false);
      handleMenuClose();
    }
  };

  const handleStatusChange = async (status) => {
    if (!selectedQuotation) return;
    
    try {
      await quotationAPI.updateStatus(selectedQuotation._id, status);
      toast.success(`Quotation marked as ${status}`);
      fetchQuotations();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update status');
    }
    handleMenuClose();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  // Calculate total sum for visible quotations
  const totalSum = quotations.reduce((sum, q) => sum + (q.grandTotal || 0), 0);

  // Handle row selection
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedRows(quotations.map(q => q._id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedRows(prev => 
      prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]
    );
  };

  return (
    <Box sx={{ backgroundColor: '#f5f5f5', minHeight: 'calc(100vh - 64px)' }}>
      {/* Page Header */}
      <PageHeader
        title="Quotations"
        newButtonText="New"
        onNew={() => navigate('/quotations/new')}
        showNewButton={true}
        search={search}
        onSearchChange={(e) => setSearch(e.target.value)}
        searchPlaceholder="Search quotations..."
        page={page}
        rowsPerPage={rowsPerPage}
        total={total}
        onPageChange={setPage}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        viewModes={['list', 'kanban']}
        onSettingsClick={(e) => setSettingsMenuAnchor(e.currentTarget)}
        additionalActions={
          <>
            {(isAdmin() || isManager()) && (
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select
                  value={mineFilter ? 'mine' : 'all'}
                  onChange={(e) => {
                    setMineFilter(e.target.value === 'mine');
                    setPage(0);
                  }}
                  sx={{
                    fontSize: '13px',
                    height: 32,
                    '& .MuiSelect-select': { py: 0.75, px: 1.5 },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#dee2e6' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#875A7B' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#875A7B' },
                  }}
                >
                  <MenuItem value="all">Quotations</MenuItem>
                  <MenuItem value="mine">My Quotations</MenuItem>
                </Select>
              </FormControl>
            )}
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
                displayEmpty
                sx={{
                  fontSize: '13px',
                  height: 32,
                  '& .MuiSelect-select': { py: 0.75, px: 1.5 },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#dee2e6' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#875A7B' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#875A7B' },
                }}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="sent">Quotation Sent</MenuItem>
                <MenuItem value="accepted">Accepted</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select
                value={locationFilter}
                onChange={(e) => {
                  setLocationFilter(e.target.value);
                  setPage(0);
                }}
                displayEmpty
                sx={{
                  fontSize: '13px',
                  height: 32,
                  '& .MuiSelect-select': {
                    py: 0.75,
                    px: 1.5,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#dee2e6',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#875A7B',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#875A7B',
                  },
                }}
              >
                <MenuItem value="">All Locations</MenuItem>
                {locations.map((loc) => (
                  <MenuItem key={loc} value={loc}>{loc}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* Hidden file input for import */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".csv"
              style={{ display: 'none' }}
            />
          </>
        }
      />

      {/* Content Area */}
      {viewMode === 'kanban' ? (
        /* Kanban/Card View */
        <Box sx={{ p: 2, bgcolor: '#f5f5f5' }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress />
            </Box>
          ) : quotations.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography color="text.secondary" sx={{ fontSize: '14px' }}>
                No quotations found.{' '}
                <Button
                  size="small"
                  onClick={() => navigate('/quotations/new')}
                  sx={{ textTransform: 'none', color: '#875A7B' }}
                >
                  Create your first quotation
                </Button>
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {quotations.map((quotation) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={quotation._id}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: '1px solid #dee2e6',
                      borderTop: '3px solid #00796b',
                      boxShadow: 'none',
                      bgcolor: 'white',
                      '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
                    }}
                    onClick={() => navigate(`/quotations/${quotation._id}`)}
                  >
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      {/* Customer Name and Amount */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Typography sx={{ 
                          fontSize: '13px', 
                          fontWeight: 700, 
                          color: '#00796b',
                          textTransform: 'uppercase',
                          lineHeight: 1.3,
                          flex: 1,
                          pr: 1,
                        }}>
                          {quotation.customer?.name || quotation.customerSnapshot?.name || 'N/A'}
                        </Typography>
                        <Typography sx={{ 
                          fontSize: '13px', 
                          fontWeight: 400, 
                          color: '#333',
                          whiteSpace: 'nowrap',
                        }}>
                          {formatCurrency(quotation.grandTotal)}
                        </Typography>
                      </Box>

                      {/* Quotation Number, Date and Status */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography sx={{ fontSize: '12px', color: '#666', fontWeight: 500 }}>
                            {quotation.quotationNumber}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                            <Typography sx={{ fontSize: '11px', color: '#999' }}>
                              {new Date(quotation.quotationDate).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                              })} {new Date(quotation.quotationDate).toLocaleTimeString('en-GB', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </Typography>
                            <AccessTime sx={{ fontSize: 14, color: '#999' }} />
                          </Box>
                        </Box>
                        <Chip 
                          label="Quotation" 
                          size="small" 
                          sx={{ 
                            height: 24, 
                            fontSize: '11px', 
                            bgcolor: 'transparent',
                            color: '#00796b',
                            border: '1px solid #00796b',
                            fontWeight: 500,
                          }} 
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      ) : (
        /* List View - Table */
        <Box sx={{ backgroundColor: 'white', overflowX: 'auto' }}>
          <TableContainer>
            <Table size="small" sx={{ minWidth: { xs: 800, md: 'auto' } }}>
              <TableHead>
                <TableRow sx={tableHeadRowStyles}>
                  <TableCell padding="checkbox" sx={{ width: 40 }}>
                    <input 
                      type="checkbox" 
                      onChange={handleSelectAll}
                      checked={selectedRows.length === quotations.length && quotations.length > 0}
                      style={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell>Number</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Salesperson</TableCell>
                  <TableCell align="center" sx={{ width: 80 }}>Activities</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Company</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : quotations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary" sx={{ fontSize: '14px' }}>
                      No quotations found.{' '}
                      <Button
                        size="small"
                        onClick={() => navigate('/quotations/new')}
                        sx={{ textTransform: 'none', color: '#875A7B' }}
                      >
                        Create your first quotation
                      </Button>
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                quotations.map((quotation) => (
                  <TableRow
                    key={quotation._id}
                    hover
                    selected={selectedRows.includes(quotation._id)}
                    sx={tableBodyRowStyles}
                    onClick={() => navigate(`/quotations/${quotation._id}`)}
                  >
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <input 
                        type="checkbox" 
                        checked={selectedRows.includes(quotation._id)}
                        onChange={() => handleSelectRow(quotation._id)}
                        style={{ cursor: 'pointer' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography sx={primaryLinkStyles}>
                        {quotation.quotationNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={standardTextStyles}>
                        {new Date(quotation.quotationDate).toLocaleString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true,
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ ...standardTextStyles, fontWeight: 500 }}>
                        {quotation.customer?.name || quotation.customerSnapshot?.name || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={tableAvatarStyles}>
                          {(quotation.salesPerson?.name || 'A').charAt(0)}
                        </Avatar>
                        <Typography sx={standardTextStyles}>
                          {quotation.salesPerson?.name || 'Admin'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" sx={{ color: '#adb5bd' }}>
                        <AccessTime sx={{ fontSize: 18 }} />
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      <Typography sx={standardTextStyles}>
                        Tech 4 Logic Private Limited
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>
                        {formatCurrency(quotation.grandTotal)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip status={quotation.status} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {/* Total Sum Row */}
        {!loading && quotations.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1, borderTop: '1px solid #dee2e6' }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#212529', pr: 2 }}>
              {formatCurrency(totalSum)}
            </Typography>
          </Box>
        )}
        </Box>
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          navigate(`/quotations/${selectedQuotation?._id}`);
          handleMenuClose();
        }}>
          <Edit sx={{ mr: 1 }} fontSize="small" /> View / Edit
        </MenuItem>
        <MenuItem onClick={handleDownloadPDF} disabled={pdfDownloadLoading}>
          {pdfDownloadLoading
            ? <CircularProgress size={16} sx={{ mr: 1 }} />
            : <PictureAsPdf sx={{ mr: 1 }} fontSize="small" />}
          {pdfDownloadLoading ? 'Generating PDF...' : 'Download PDF'}
        </MenuItem>
        <MenuItem onClick={handleDuplicate}>
          <ContentCopy sx={{ mr: 1 }} fontSize="small" /> Duplicate
        </MenuItem>
        {selectedQuotation?.status === 'draft' && (
          <MenuItem onClick={() => handleStatusChange('sent')}>
            <Send sx={{ mr: 1 }} fontSize="small" /> Mark as Sent
          </MenuItem>
        )}
        {['sent', 'viewed'].includes(selectedQuotation?.status) && (
          <>
            <MenuItem onClick={() => handleStatusChange('accepted')}>
              <CheckCircle sx={{ mr: 1 }} fontSize="small" color="success" /> Accept
            </MenuItem>
            <MenuItem onClick={() => handleStatusChange('rejected')}>
              <Cancel sx={{ mr: 1 }} fontSize="small" color="error" /> Reject
            </MenuItem>
          </>
        )}
        {canDelete() && (
          <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
            <Delete sx={{ mr: 1 }} fontSize="small" /> Delete
          </MenuItem>
        )}
      </Menu>

      {/* Settings Menu - Import/Export */}
      <Menu
        anchorEl={settingsMenuAnchor}
        open={Boolean(settingsMenuAnchor)}
        onClose={() => setSettingsMenuAnchor(null)}
        PaperProps={{
          sx: {
            minWidth: 180,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            mt: 0.5,
          }
        }}
      >
        <MenuItem onClick={() => { handleExportAll(); setSettingsMenuAnchor(null); }} disabled={exporting}>
          <GetApp sx={{ fontSize: 18, color: '#666', mr: 1.5 }} />
          {exporting ? 'Exporting...' : 'Export All (CSV)'}
        </MenuItem>
        <MenuItem onClick={() => { handleImportClick(); setSettingsMenuAnchor(null); }}>
          <Upload sx={{ fontSize: 18, color: '#666', mr: 1.5 }} />
          Import from CSV
        </MenuItem>
      </Menu>

      {/* Bulk Actions Bar */}
      {canDelete() && (
        <BulkActionBar
          selectedCount={selectedRows.length}
          onClearSelection={() => setSelectedRows([])}
          onDelete={handleBulkDelete}
        />
      )}

      {/* Single Delete Confirmation */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); handleMenuClose(); }}
        onConfirm={confirmDelete}
        title="Delete Quotation"
        message={`Are you sure you want to delete ${selectedQuotation?.quotationNumber}? This action cannot be undone.`}
        confirmText="Delete"
        type="danger"
        loading={deleting}
      />

      {/* Bulk Delete Confirmation */}
      <ConfirmDialog
        open={bulkDeleteDialogOpen}
        onClose={() => setBulkDeleteDialogOpen(false)}
        onConfirm={confirmBulkDelete}
        title="Delete Quotations"
        message={`Are you sure you want to delete ${selectedRows.length} quotation(s)? This action cannot be undone.`}
        confirmText={`Delete ${selectedRows.length} Quotation(s)`}
        type="danger"
        loading={deleting}
      />
    </Box>
  );
};

export default QuotationList;
